import Cookies from 'js-cookie';
import _isFunction from 'lodash/isFunction';
import memoize from 'lodash/memoize';
import uuid from 'uuid';

import { TEAM_ACCOUNT_TYPES } from 'constants/accountTypes';
import { isDev } from 'constants/environment';
import {
  ALLOWLIST_GOOGLE_ANALYTICS,
  INCLUDED_EVENTS_FROM_INTERCOM,
} from 'constants/events';
import { ELEVIO_ACCOUNT_ID } from 'constants/runtimeConfig';
import { LOOM_VERSION } from 'constants/versions';
import { isMobile } from 'utilities/device';
import { store } from 'utilities/store';

import * as logger from './loggerx';
import { LOOM_ANON_COOKIE_DOMAIN } from '../constants/routes';
import { DEBUG_ANALYTICS } from '../../../shared/constants/analytics';
import { KEY_AJS_ANON_ID } from '../../../shared/constants/cookie';
import { USER } from '../../../shared/constants/ssr';

// maximum time to wait before calling the callback
// this because the callback may never be called when ad blocker is running
// so we call it if this much time has passed and it hasn't been called yet
const MAX_WAIT_BEFORE_CALLBACK_MS = 150;
// wrapper for Segment's analytics.js to check for its existence

export const ready = function (callback) {
  if (typeof window?.analytics?.ready !== 'function') {
    return callback?.();
  }

  let fired = false;

  function _onReady() {
    if (fired) {
      return;
    }

    fired = true;

    callback?.();
  }

  window.analytics.ready(_onReady);

  // window.analytics will not fire the ready callback if someone has adblocker
  // on and a thirdparty library does not load - give a generous second for
  // ready to fire
  window.setTimeout(_onReady, 1000);
};

export const page = function (name, props = {}, options = {}) {
  if (typeof window?.analytics?.page !== 'function') {
    return;
  }

  // Zendesk does not support page requests
  options.integrations = {
    ...(options.integrations || {}),
    Zendesk: false,
  };

  if (DEBUG_ANALYTICS && isDev) {
    console.log(`📊 [analytics] Page: ${name}`, props);
  }

  // sometimes the store isn't initialized in time (eg Push notification click track)
  // thus we add the optional chain operator to make sure it doesn't crash the track call
  // the event will be assigned to the correct person based on the segment cookies in that
  // case
  const state = props?.state || store?.getState();

  const { is_logged_in: isLoggedIn, is_logged_in_checked: isLoggedInChecked } =
    state?.user || {};

  if (isLoggedInChecked) {
    props.is_logged_in = isLoggedIn;
  }

  // Log whether the user is on a mobile device
  props.isMobile = isMobile;

  window.analytics.page(name, props, options);
};

// callback here is particularly useful where we trigger a page unload
// immediately following a track call. this ensures the post will
// execute before the unload and avoid raciness
export const track = function (evt, props = {}, callback) {
  try {
    if (typeof window?.analytics?.track !== 'function') {
      callback?.();

      return;
    }

    const options = {};

    const ajsId = Cookies.get(KEY_AJS_ANON_ID);

    // not all events are required in intercom and they
    // cause events to not go through due to their event
    // limit.
    if (!INCLUDED_EVENTS_FROM_INTERCOM.includes(evt)) {
      options.integrations = {
        Intercom: false,
      };
    }

    // There are limits to the Google Analytics API volume so we are
    // including this allowlist to explicitly receive a subset of our
    // events from Segment
    if (!ALLOWLIST_GOOGLE_ANALYTICS.includes(evt)) {
      options.integrations = {
        ...(options.integrations || {}),
        'Google Analytics': false,
      };
    }

    // Zendesks track API is in an early access program right now
    // which one must sign up for to use and we have not signed up for it yet
    // https://segment.com/docs/destinations/zendesk/#track
    options.integrations = {
      ...(options.integrations || {}),
      Zendesk: false,
    };

    // sometimes the store isn't initialized in time (eg Push notification click track)
    // thus we add the optional chain operator to make sure it doesn't crash the track call
    // the event will be assigned to the correct person based on the segment cookies in that
    // case
    const state = props.state ? props.state : store?.getState();

    const {
      is_logged_in: isLoggedIn,
      is_logged_in_checked: isLoggedInChecked,
      current: currentUser,
    } = state?.user || {};

    //  if user has a selected workspace attach to event property
    const selectedWorkspaceId = state?.workspace?.selectedWorkspace?.id;

    if (isLoggedIn) {
      if (currentUser.is_sdk_shared_user) {
        props.sdkPartnerId = currentUser.default_workspace_id;
      } else {
        if (props.userId) {
          logger.warning(
            new Error(
              'Avoid logging a userId field in an analytics event explicitly. This overwrites the userId that is included in all events by default.'
            ),
            {
              fields: props,
            }
          );
        }

        props.userId = currentUser.id;
        props.organization_id = selectedWorkspaceId || props.organization_id;
      }
    } else {
      options.integrations = {
        ...(options.integrations || {}),
      };
    }

    // Log whether the user is logged in or not
    if (isLoggedInChecked) {
      props.is_logged_in = isLoggedIn;
    }

    // Log whether the user is on a mobile device
    props.isMobile = isMobile;

    if (typeof callback === 'function') {
      let callbackHasBeenCalled = false;

      const originalCallback = callback;

      callback = () => {
        if (!callbackHasBeenCalled) {
          callbackHasBeenCalled = true;
          originalCallback();
        }
      };

      setTimeout(callback, MAX_WAIT_BEFORE_CALLBACK_MS);
    }

    if (DEBUG_ANALYTICS && isDev) {
      console.log(`📊 [analytics] Track: ${evt}`, props);
    }

    window.analytics.track(
      evt,
      {
        ...(props || {}),
        ajsId,
      },
      options,
      callback
    );
  } catch (err) {
    console.error('error in analytics.track', err);
    callback?.();
  }
};

export const identify = function (userID, traits, options = {}) {
  // seed the ajs_anonymous_id cookie regardless of
  // window.analytics being present. This helps us
  // when it comes to having anon uuids in server req
  // credentials
  getAnalyticsIds();

  if (typeof window?.analytics?.identify !== 'function') {
    return;
  }

  if (!userID) {
    options.integrations = {
      ...(options.integrations || {}),
      Zendesk: false,
      Elevio: false,
    };
  }

  // due to zendesk api rate limiting we're removing all udpates
  // from the website temporarily till we figure out a better
  // solution
  options.integrations = {
    ...(options.integrations || {}),
    Zendesk: false,
  };

  // COMMENTING OUT DUE TO TEMP RESTRICTION ADDED ABOVE
  // const hasFirstName = traits && traits.firstName;
  // const hasLastName = traits && traits.lastName;
  // const hasName = traits && traits.name;

  // // require that "name" or "firstName" && "lastName" traits be set in identify call for
  // // it to be sent to Zendesk
  // // https://segment.com/docs/destinations/zendesk/#identify
  // if (userID && !(hasFirstName && hasLastName) && !hasName) {
  //   options.integrations = {
  //     ...(options.integrations || {}),
  //     Zendesk: false,
  //   };
  // }

  window.analytics.identify(
    userID,
    {
      ...traits,
      website_version: LOOM_VERSION,
    },
    options
  );
};

export const clearCookies = function () {
  if (typeof window?.analytics?.reset !== 'function') {
    return;
  }

  window.analytics.reset();
};

export const activateProfitwell = function (email) {
  if (!window?.profitwell) {
    return;
  }

  window.profitwell('user_email', email);
  window.profitwell('cq_get');
};

export const identifyUserleap = function (user) {
  if (!window?.UserLeap) {
    return;
  }

  window.UserLeap('setUserId', user?.id);
  window.UserLeap('setEmail', user?.email);
};

export const signOutUserLeap = function () {
  if (!window?.UserLeap) {
    return;
  }

  window.UserLeap('logoutUser');
};

export const trackSprigEvent = event => {
  if (!window?.UserLeap) {
    return;
  }

  window.UserLeap('track', event);
};

// ad blockers sometimes block our analytics libs from loading
export const amplitudeLoaded = function () {
  return (
    window.amplitude != null && window.amplitude.getInstance().options != null
  );
};

export const segmentLoaded = function () {
  return window.analytics != null && _isFunction(window.analytics.user);
};

const _getAnonIdIfNoSegment = function () {
  const encoded = Cookies.get(KEY_AJS_ANON_ID);

  const decoded = (encoded || '').replace(/"/g, '');

  if (!encoded || decoded === 'null' || decoded === 'undefined') {
    const newUid = uuid.v4();

    Cookies.set(KEY_AJS_ANON_ID, `"${newUid}"`, {
      domain: LOOM_ANON_COOKIE_DOMAIN,
      expires: 365,
    });

    logger.warning(
      'anonymous id was not available from cookie referenced in _getAnonIdIfNoSegment, spawned a random id and set cookie',
      { anonymousId: newUid }
    );

    return newUid;
  }

  return decoded;
};

export const getAnalyticsIds = function () {
  let anonID = null;
  let userID = null;

  // we manually set the anonymous ID in case the library isn't loaded
  // due to adblockers etc.
  if (segmentLoaded()) {
    anonID = window.analytics.user().anonymousId();
    userID = window.analytics.user().id();
  } else {
    anonID = _getAnonIdIfNoSegment();
  }

  let deviceID = null;

  if (amplitudeLoaded()) {
    deviceID = window.amplitude.getInstance().options.deviceId;
  }

  return { anonID, deviceID, userID };
};

let elevioSnippetAdded = false;

export const waitForElevio = memoize(() => {
  if (window._elev?.on) {
    return Promise.resolve(window._elev);
  }

  if (!elevioSnippetAdded) {
    const snippet = document.createElement('script');

    snippet.innerHTML = `!function(e,l,v,i,o,n){e[i]||(e[i]={}),e[i].account_id=n;var g,h;g=l.createElement(v),g.type="text/javascript",g.async=1,g.src=o+n,h=l.getElementsByTagName(v)[0],h.parentNode.insertBefore(g,h);e[i].q=[];e[i].on=function(z,y){e[i].q.push([z,y])}}(window,document,"script","_elev","https://cdn.elev.io/sdk/bootloader/v4/elevio-bootloader.js?cid=","${ELEVIO_ACCOUNT_ID}");`;

    document.body.appendChild(snippet);
    elevioSnippetAdded = true;
  }

  return new Promise((resolve, reject) => {
    let tries = 0;
    const interval = setInterval(() => {
      if (window._elev?.on) {
        window._elev.on('load', _elev => {
          clearInterval(interval);

          const properties = window.analytics?.user?.().properties();
          const user_hash = window[USER]?.elevio_hash;

          if (properties && user_hash) {
            const { email, name, ...traits } = properties;
            const user = { email, name, user_hash, traits };

            if (TEAM_ACCOUNT_TYPES.includes(traits.accountType)) {
              user.groups = ['LoomTeamAccount'];
            }

            window._elev?.setUser?.(user);
            window._elev?.setSettings({
              articleFeedbackEnabled: false,
            });
          }

          resolve(_elev);
        });
      } else if (tries > 100) {
        clearInterval(interval);
        reject('Elevio was not loaded on time');
      }

      tries++;
    }, 50);
  });
});
